import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ron } from "@/lib/api";
import { formatDate } from "@/lib/date";
import { Trash2, Trophy, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { SalesPeriodFilter } from "@/components/SalesPeriodFilter";
import SalesExportDialog from "@/components/SalesExportDialog";
import { useAuth } from "@/context/AuthContext";
import { RateBadge } from "@/components/RateBadge";

export default function Sales() {
  const [items, setItems] = useState([]);
  const [cars, setCars] = useState({});
  const [clients, setClients] = useState({});
  const [period, setPeriod] = useState({ preset: "all", from: "", to: "" });
  const [exportOpen, setExportOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  async function load() {
    const [s, c, cl] = await Promise.all([
      api.get("/sales", { params: { date_from: period.from || undefined, date_to: period.to || undefined } }),
      api.get("/cars"),
      api.get("/clients"),
    ]);
    setItems(s.data);
    setCars(Object.fromEntries(c.data.map((x) => [x.id, x])));
    setClients(Object.fromEntries(cl.data.map((x) => [x.id, x])));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("data:refresh", h);
    return () => window.removeEventListener("data:refresh", h);
  }, [period.from, period.to]);

  async function remove(id) {
    if (!window.confirm("Ștergi vânzarea?")) return;
    await api.delete(`/sales/${id}`);
    toast.success("Șters");
    load();
  }

  const total = items.reduce((s, x) => s + (x.sale_price || 0), 0);

  return (
    <div className="sm:pl-52 space-y-4" data-testid="sales-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Tranzacții</div>
          <h1 className="font-display text-3xl tracking-tight font-medium">Vânzări</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm tabular" data-testid="sales-total">Total: <strong>{ron(total)}</strong></div>
          {isAdmin && (
            <button
              onClick={() => setExportOpen(true)}
              data-testid="sales-export-excel-btn"
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
            >
              <FileSpreadsheet className="w-4 h-4" /> Export Excel
            </button>
          )}
        </div>
      </div>

      <SalesPeriodFilter period={period} onChange={setPeriod} />
      <SalesExportDialog open={exportOpen} onOpenChange={setExportOpen} period={period} onApply={setPeriod} />

      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {items.length === 0 && (
          <div className="p-10 text-center">
            <Trophy className="w-10 h-10 mx-auto text-slate-300" />
            <div className="mt-3 font-display text-lg">{period.preset === "all" ? "Nicio vânzare încă" : "Nicio vânzare în perioada aleasă"}</div>
            <div className="text-sm text-slate-500 mt-1">{period.preset === "all" ? 'Marchează o mașină ca „vândută" din fișa ei.' : "Alege altă perioadă."}</div>
          </div>
        )}
        {items.map((s) => {
          const car = cars[s.car_id];
          const client = s.client_id ? clients[s.client_id] : null;
          return (
            <div key={s.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50" data-testid={`sale-row-${s.id}`}>
              <div>
                {car ? (
                  <Link to={`/cars/${car.id}`} className="font-medium text-sm hover:underline">
                    {car.make} {car.model} <span className="text-slate-400">· {s.vin?.slice(-6) || car.year || "—"}</span>
                  </Link>
                ) : (
                  <div className="font-medium text-sm text-slate-500">VIN: {s.vin || "—"}</div>
                )}
                <div className="text-xs text-slate-500">
                  {client ? (
                    <Link to={`/clients/${client.id}`} className="hover:underline">{client.name}</Link>
                  ) : "Fără client asociat"}
                  {" · "}{formatDate(s.sale_date)}
                </div>
                {s.source && (
                  <div className="mt-1">
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                      📍 {s.source}
                    </span>
                  </div>
                )}
                {s.notes && <div className="text-xs text-slate-400 mt-0.5">{s.notes}</div>}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className="inline-flex items-center gap-1.5"><RateBadge doc={s} testid={`sale-rate-badge-${s.id}`} /><span className="tabular text-sm font-semibold text-emerald-600">{ron(s.sale_price)}</span></span>
                  {s.profit_snapshot !== undefined && s.profit_snapshot !== null && (
                    <div className="text-[10px] text-slate-400 tabular leading-none mt-0.5">
                      profit blocat: <span className={s.profit_snapshot >= 0 ? "text-emerald-600" : "text-rose-600"}>{ron(s.profit_snapshot)}</span>
                    </div>
                  )}
                </div>
                <button onClick={() => remove(s.id)} className="text-slate-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
